/**
 * Field-complete "Strong" bar — shared completeness contract for the
 * Coverage Matrix, CAF resolver, and Author Evidence dialog.
 *
 * A requirement reads **Strong** only when:
 *   - all `required: true` authoredSchema fields are filled, AND
 *   - ≥80% of structured fields (notes excluded) are filled.
 *
 * Mirrors `inferAuthoredConfidence` / CAR-8 thresholds so save-time
 * inference and read-time matrix status stay in lockstep.
 */
import type { ChecklistItem, RankedChecklistMatch } from '../../config/documentChecklists';
import type { SiteDocument } from '../../types/siteRag';
import { scopeGridRequirementEvidence } from './scopeGridRequirementEvidence';
import type { SiteEvidenceDocument } from '../../types/siteEvidence';
import type { ExtractedClaim } from '../../types/siteContext';
import type { AuthoredFieldDef } from '../../types/authoredEvidence';
import { isFieldFilled } from '../../types/authoredEvidence';
import { computeRequirementFieldCoverage } from '../evidenceLineage/requirementLineage';
import { resolveReportFields, isTariffRequirement, isContextualTariffField } from './resolveReportFields';
import { resolveRequirementFields } from '../evidenceLineage/requirementToFields';

/** Fraction of structured fields required for the Strong bar. */
export const STRONG_STRUCTURED_FRACTION = 0.8;

/** Fraction of structured fields required for the Adequate bar. */
export const ADEQUATE_STRUCTURED_FRACTION = 0.5;

export interface RequirementCompleteness {
  requiredTotal: number;
  requiredFilled: number;
  structuredTotal: number;
  structuredFilled: number;
  meetsStrongBar: boolean;
  meetsAdequateBar: boolean;
  meetsTentativeBar: boolean;
}

export type SedFieldCompleteness = RequirementCompleteness & {
  /** False when `REQUIREMENT_TO_FIELDS` has no entry for this requirement. */
  hasFieldRegistry: boolean;
};

function structuredSchemaFields(
  schema: readonly AuthoredFieldDef[] | undefined,
): readonly AuthoredFieldDef[] {
  return (schema ?? []).filter(f => f.key !== 'notes');
}

function computeCompletenessBars(args: {
  requiredTotal: number;
  requiredFilled: number;
  structuredTotal: number;
  structuredFilled: number;
  hasNotes: boolean;
}): RequirementCompleteness {
  const { requiredTotal, requiredFilled, structuredTotal, structuredFilled, hasNotes } = args;
  // Vacuously TRUE when a schema declares no required fields ("all zero of
  // them are filled"). Harmless inside `meetsStrongBar`, which ANDs it with
  // the structured-fraction test — that conjunct still does the gating.
  const requiredAllFilled =
    requiredTotal === 0 || requiredFilled === requiredTotal;
  // The required-field bar as an INDEPENDENT pass signal, for use in ORs.
  // A schema with no required fields must NOT clear it: "all zero required
  // fields are filled" is not evidence of anything.
  //
  // Using `requiredAllFilled` here instead was a trust bug. Only five
  // schemas in the checklist registry declare any `required: true` field,
  // so for every other requirement `meetsAdequateBar` short-circuited to
  // true and the row read `adequate` on a *totally empty* SED — 0/17 fields
  // filled, no linked sources. That inflated the Evidence-tab readiness
  // gauge (80% shown vs 46% real on Redhead Industrial Park) and made the
  // Evidence tab contradict Path to GO for the same site. The signature was
  // a non-monotonic result: `meetsAdequateBar: true` while
  // `meetsTentativeBar: false` — the row cleared the higher bar and failed
  // the lower one.
  const requiredBarSatisfied = requiredTotal > 0 && requiredFilled === requiredTotal;
  const structuredFraction =
    structuredTotal > 0 ? structuredFilled / structuredTotal : 0;

  if (structuredTotal === 0) {
    return {
      requiredTotal,
      requiredFilled,
      structuredTotal,
      structuredFilled,
      meetsStrongBar: false,
      meetsAdequateBar: hasNotes,
      meetsTentativeBar: hasNotes,
    };
  }

  return {
    requiredTotal,
    requiredFilled,
    structuredTotal,
    structuredFilled,
    meetsStrongBar:
      requiredAllFilled && structuredFraction >= STRONG_STRUCTURED_FRACTION,
    meetsAdequateBar:
      requiredBarSatisfied || structuredFraction >= ADEQUATE_STRUCTURED_FRACTION,
    meetsTentativeBar: structuredFilled > 0 || hasNotes,
  };
}

function mergeStructuredFieldsFromClaims(
  claims: ReadonlyArray<ExtractedClaim>,
): Record<string, string | number | boolean> {
  const merged: Record<string, string | number | boolean> = {};
  for (const claim of claims) {
    const fields = claim.structuredFields ?? {};
    for (const [key, value] of Object.entries(fields)) {
      if (isFieldFilled(value)) merged[key] = value;
    }
  }
  return merged;
}

function claimHasNotes(claims: ReadonlyArray<ExtractedClaim>): boolean {
  return claims.some(c => {
    if (isFieldFilled(c.structuredFields?.notes)) return true;
    if (c.authoredKind === 'prose' && isFieldFilled(c.claim)) return true;
    if (c.authoredKind === 'prose' && isFieldFilled(c.sourceSpan)) return true;
    return false;
  });
}

/**
 * Evaluate authored-claim completeness against `requirement.authoredSchema`.
 * Recomputes at read time — persisted `extractorConfidence` is not trusted.
 */
export function evaluateAuthoredCompleteness(
  requirement: ChecklistItem,
  claims: ReadonlyArray<ExtractedClaim>,
): RequirementCompleteness {
  const schema = structuredSchemaFields(requirement.authoredSchema);
  const merged = mergeStructuredFieldsFromClaims(claims);
  const hasNotes = claimHasNotes(claims);

  const requiredFields = schema.filter(f => f.required);
  const requiredFilled = requiredFields.filter(f =>
    isFieldFilled(merged[f.key]),
  ).length;
  const structuredFilled = schema.filter(f =>
    isFieldFilled(merged[f.key]),
  ).length;

  const bars = computeCompletenessBars({
    requiredTotal: requiredFields.length,
    requiredFilled,
    structuredTotal: schema.length,
    structuredFilled,
    hasNotes,
  });

  // UAE-8 — an authored row must not fall below `'authored'` merely because
  // its schema declares no `required: true` field. A human deliberately
  // entered this data, so the provenance signal is real (unlike an empty SED
  // slot) and the violet ✎ chip depends on the row resolving to `'authored'`
  // via `meetsAdequateBar` in `computeRequirementStatus`'s Tier 1.
  //
  // This floor used to come for free from the vacuously-true
  // `requiredAllFilled` inside `meetsAdequateBar`. That vacuous term was a
  // trust bug on the SED path (see `requiredBarSatisfied` above), so the
  // authored floor is now stated explicitly here where it is actually
  // justified. Completeness still gates the tier ABOVE this floor:
  // `meetsStrongBar` is untouched, so `'authored-strong'` continues to
  // require all-required + ≥80% structured fields.
  const hasAuthoredSignal = structuredFilled > 0 || hasNotes;
  return {
    ...bars,
    meetsAdequateBar: bars.meetsAdequateBar || hasAuthoredSignal,
  };
}

/**
 * Evaluate SED / schema field completeness for Strong-tier bars.
 *
 * When `requirement.authoredSchema` declares structured fields, counts
 * use schema-first resolution (aligned SED paths fill schema slots;
 * supporting registry paths do not inflate the bar).
 */
export function evaluateSedFieldCompleteness(
  requirementId: string,
  sed: SiteEvidenceDocument | undefined,
  options?: {
    requirement?: ChecklistItem;
    authoredClaim?: ExtractedClaim;
    documents?: ReadonlyArray<SiteDocument>;
    match?: RankedChecklistMatch;
  },
): SedFieldCompleteness {
  const requirement = options?.requirement;
  // Public grid context remains visible, but absent attached source authority
  // cannot establish completion in either the matrix or CAF/readiness path.
  if (sed && requirement && (requirementId === 'utility-letter' || requirementId === 'iso-queue')) {
    sed = scopeGridRequirementEvidence({ requirement, sed, documents: options?.documents ?? [], match: options?.match });
  }
  const structuredSchema = structuredSchemaFields(requirement?.authoredSchema);
  if (requirement && structuredSchema.length > 0) {
    const resolved = resolveReportFields({
      requirement,
      sed,
      authoredClaim: options?.authoredClaim,
    });
    const requiredFields = structuredSchema.filter(f => f.required);
    const requiredFilled = requiredFields.filter(f => {
      const hit = resolved.primary.find(p => p.key === f.key);
      return hit != null && !hit.isEmpty;
    }).length;
    const bars = computeCompletenessBars({
      requiredTotal: requiredFields.length,
      requiredFilled,
      structuredTotal: resolved.structuredTotal,
      structuredFilled: resolved.structuredFilled,
      hasNotes: false,
    });
    return {
      hasFieldRegistry: true,
      ...bars,
    };
  }

  const coverage = computeRequirementFieldCoverage(requirementId, sed);
  if (isTariffRequirement(requirementId) && coverage.total > 0) {
    const fields = resolveRequirementFields(sed, requirementId, true)
      .filter(field => !isContextualTariffField(requirementId, field, sed));
    return {
      hasFieldRegistry: true,
      ...computeCompletenessBars({ requiredTotal: 0, requiredFilled: 0,
        structuredTotal: Math.max(1, fields.length),
        structuredFilled: fields.filter(field => !field.isEmpty).length, hasNotes: false }),
    };
  }
  if (coverage.total === 0) {
    return {
      hasFieldRegistry: false,
      requiredTotal: 0,
      requiredFilled: 0,
      structuredTotal: 0,
      structuredFilled: 0,
      meetsStrongBar: false,
      meetsAdequateBar: false,
      meetsTentativeBar: false,
    };
  }

  const bars = computeCompletenessBars({
    requiredTotal: 0,
    requiredFilled: 0,
    structuredTotal: coverage.total,
    structuredFilled: coverage.filled,
    hasNotes: false,
  });

  return {
    hasFieldRegistry: true,
    ...bars,
    meetsStrongBar: bars.structuredFilled / coverage.total >= STRONG_STRUCTURED_FRACTION,
    meetsAdequateBar: bars.structuredFilled / coverage.total >= ADEQUATE_STRUCTURED_FRACTION,
    meetsTentativeBar: coverage.filled > 0,
  };
}

/**
 * Map completeness bars to a CAF / matrix-style status label for doc fills.
 * A tier-1 doc hit floors at `adequate` when field completeness is UNKNOWN
 * (no field registry) — matching legacy tag-match / DCT behaviour.
 *
 * When a field registry DOES exist it is authoritative: a registry that
 * reports zero filled fields floors at `tentative`, never `adequate`. The
 * doc matched, so the row is not `missing`; but nothing was extracted from
 * it, so claiming adequate evidence would be a fabrication. This mirrors
 * `computeRequirementStatus`'s Tier-3 rule — a DCT match without slot
 * population caps at `tentative`.
 */
export function docCoverageStatusFromCompleteness(
  completeness: SedFieldCompleteness,
): 'strong' | 'adequate' | 'tentative' {
  if (completeness.hasFieldRegistry) {
    if (completeness.meetsStrongBar) return 'strong';
    if (completeness.meetsAdequateBar) return 'adequate';
    // Registry present but no bar met (incl. zero filled fields) — the
    // matched doc yielded nothing. Floor at tentative, do NOT fall through
    // to the unknown-completeness `adequate` default below.
    return 'tentative';
  }
  if (completeness.meetsStrongBar) return 'strong';
  return 'adequate';
}

/**
 * Map completeness bars to authored envelope status.
 * When a claim exists but completeness is thin, floor at `authored`.
 */
export function authoredCoverageStatusFromCompleteness(
  completeness: RequirementCompleteness,
): 'strong' | 'authored' | 'tentative' {
  if (completeness.meetsStrongBar) return 'strong';
  if (completeness.meetsAdequateBar) return 'authored';
  if (completeness.meetsTentativeBar) return 'tentative';
  return 'authored';
}
